import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faDollarSign } from '@fortawesome/pro-solid-svg-icons';
import { faUtensils } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Menu from 'classes/Menu/Search/Menu';
import Recipe from 'classes/Menu/Search/Recipe';
import MenuPlan from 'classes/MenuPlan/Detail/MenuPlan';
import 'components/desktop/_general/AddMenuOrRecipe/MenusList/SearchResultList.scss';
import SearchResultThumbnailCard from 'components/desktop/_general/AddMenuOrRecipe/MenusList/SearchResultThumbnailCard';
import BtnGroup from 'components/desktop/_general/Button/BtnGroup/BtnGroup';
import { BtnData } from 'components/desktop/_general/Button/BtnGroup/BtnGroup';
import { RenderIf } from 'components/desktop/_general/Conditional/RenderIf';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import ENTITLEMENTS from 'enums/entitlements';
import { ValueScope } from 'enums/valueScope';
import { getNumberString } from 'functions/numberToString';
import { getPriceString } from 'functions/numberToString';
import { getValueScopeLabel } from 'functions/valueScopeConversion';
import { RootState } from 'reducers/rootReducer';

interface IProps {
	SourceOfAddingType: 'menu' | 'menuPlan' | 'menuCopy';
	SourceOfAddingId: string | null;
	SourceOfAddingRow: number;
	SourceOfAddingCol: number;
	finalizeAdding: () => void;
	menu: Menu;
}

const SearchResultListRow = (props: IProps) => {
	const { t } = useTranslation();
	const reduxCultureCode: string = useSelector((state: RootState) => state.cultureCode);
	const reduxCurrency: string = useSelector((state: RootState) => state.currency);

	const menuPlanId: string | null = props.SourceOfAddingId;
	const rowIndex: string | undefined = props.SourceOfAddingRow.toString();
	const day: string | undefined = props.SourceOfAddingCol.toString();
	const [isLoading, setIsLoading] = useState(false);

	const getValueScope = (): string => {
		return getValueScopeLabel(ValueScope.Person, null, true);
	};

	const renderInformation = (): JSX.Element => {
		return (
			<div className="d-flex flex-column justify-content-between information">
				<div>
					<div id="title-info-box">
						<strong>
							{props.menu.nameTranslation
								? props.menu.nameTranslation
								: t('_general:UNKNOWN_TITLE')}
						</strong>
					</div>
					<div>
						{!!props.menu.calories && (
							<>
								<span className="icon">
									<FontAwesomeIcon icon={faUtensils as IconProp} />
								</span>
								<b>
									{getNumberString(props.menu.calories, reduxCultureCode, true)}
								</b>
								{` ${t('_general:CALORIES')} ${getValueScope()}`}
							</>
						)}
						&nbsp;
					</div>
					<div>
						{!!props.menu.price && (
							<RenderIf entitlements={[ENTITLEMENTS.PRICE]}>
								<>
									<span className="icon">
										<FontAwesomeIcon icon={faDollarSign as IconProp} />
									</span>
									<b>{getPriceString(props.menu.price, reduxCultureCode)}</b>
									{` ${reduxCurrency} ${getValueScope()}`}
								</>
							</RenderIf>
						)}
						&nbsp;
					</div>
				</div>
				<div>{renderButtons()}</div>
			</div>
		);
	};

	const renderButtons = (): JSX.Element => {
		return <BtnGroup add={getButtonDataAdd()} />;
	};

	const getButtonDataAdd = (): BtnData | undefined => {
		if (menuPlanId && rowIndex && day) {
			return {
				onClick: addToMenu,
			};
		}
	};

	const addToMenu = async (): Promise<void> => {
		setIsLoading(true);
		if (menuPlanId && rowIndex && day) {
			const menuPlan = new MenuPlan();
			await menuPlan.callApiGet(menuPlanId, reduxCurrency);
			menuPlan.setMenuToRow(Number(day), Number(rowIndex), props.menu.id);
			await menuPlan.saveToApi(reduxCurrency);
		}
		props.finalizeAdding();
		setIsLoading(false);
	};

	const getAllRecipes = (): Recipe[] => {
		if (props.menu.recipeList.all) {
			return props.menu.recipeList.all;
		}
		return [];
	};

	return (
		<div className="d-flex flex-row" style={{ gap: '10px' }}>
			<LoadingAnimation isLoading={isLoading} isModal></LoadingAnimation>
			{renderInformation()}
			<div className="recipe-row">
				<div className="d-flex flex-row" style={{ gap: '10px' }}>
					{getAllRecipes().map((e: Recipe, i: number) => {
						return (
							<div key={i}>
								<SearchResultThumbnailCard recipe={e} />
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};

export default SearchResultListRow;
